import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from '../hooks/useCategories';
import { useTodos } from '../hooks/useTodos';
import { useTimeLogs } from '../hooks/useTimeLogs';
import { Category } from '../types/api';
import { useToast } from '../components/ToastProvider';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import { getApiErrorMessage } from '../utils/errors';
import { formatDuration, parseDurationToSeconds } from '../utils/datetime';
import { accentForId } from '../utils/colors';

const CategoriesPage: React.FC = () => {
  const { data: categories, isLoading, isError, error, refetch } = useCategories();
  const { data: todos } = useTodos();
  const { data: timeLogs } = useTimeLogs();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const { showToast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Category | null>(null);

  const taskCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const t of todos ?? []) {
      if (t.category != null) counts.set(t.category, (counts.get(t.category) ?? 0) + 1);
    }
    return counts;
  }, [todos]);

  // Total seconds logged per category (a time log's own `category`, which is set
  // independently of its linked task's category).
  const timeByCategory = useMemo(() => {
    const totals = new Map<number, number>();
    for (const log of timeLogs ?? []) {
      if (log.category != null) {
        totals.set(log.category, (totals.get(log.category) ?? 0) + parseDurationToSeconds(log.time_logged));
      }
    }
    return totals;
  }, [timeLogs]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '' });
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (category: Category) => {
    setEditing(category);
    setForm({ name: category.name, description: category.description ?? '' });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const payload = { name: form.name.trim(), description: form.description.trim() };
    const onError = (err: unknown) => setFormError(getApiErrorMessage(err));

    if (editing) {
      updateCategory.mutate(
        { id: editing.id, data: payload },
        {
          onSuccess: () => {
            setDialogOpen(false);
            showToast('Category updated');
          },
          onError,
        }
      );
    } else {
      createCategory.mutate(payload, {
        onSuccess: () => {
          setDialogOpen(false);
          showToast('Category created');
        },
        onError,
      });
    }
  };

  const confirmDelete = () => {
    if (!toDelete) return;
    deleteCategory.mutate(toDelete.id, {
      onSuccess: () => {
        setToDelete(null);
        showToast('Category deleted');
      },
      onError: (err) => {
        setToDelete(null);
        showToast(getApiErrorMessage(err), 'error');
      },
    });
  };

  const saving = createCategory.isPending || updateCategory.isPending;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1080, width: '100%', mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">Categories</Typography>
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Loading…' : `${(categories ?? []).length} categories`}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
          New category
        </Button>
      </Stack>

      {isError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
        >
          {getApiErrorMessage(error, 'Could not load your categories.')}
        </Alert>
      ) : isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: 3.5 }} />
          ))}
        </Box>
      ) : (categories ?? []).length === 0 ? (
        <Card>
          <EmptyState
            icon={<FolderRoundedIcon />}
            title="No categories yet"
            description="Group your tasks and time logs by area — e.g. Work, Personal, Learning."
            actionLabel="Create a category"
            onAction={openCreate}
          />
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {(categories ?? []).map((category) => {
            const accent = accentForId(category.id);
            const count = taskCounts.get(category.id) ?? 0;
            const seconds = timeByCategory.get(category.id) ?? 0;
            return (
              <Card
                key={category.id}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'box-shadow 120ms ease',
                  '&:hover': { boxShadow: '0 4px 16px rgba(31,35,51,0.08)' },
                }}
              >
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, bgcolor: accent }} />
                <CardContent sx={{ pt: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: `${accent}1A`,
                        color: accent,
                        flexShrink: 0,
                      }}
                    >
                      <FolderRoundedIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                        {category.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-word', minHeight: 20 }}
                      >
                        {category.description || 'No description'}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(category)} aria-label={`Edit ${category.name}`}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setToDelete(category)}
                          aria-label={`Delete ${category.name}`}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}>
                    <Chip
                      size="small"
                      label={`${count} task${count === 1 ? '' : 's'}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      icon={<ScheduleRoundedIcon />}
                      label={`${formatDuration(seconds)} logged`}
                      variant="outlined"
                    />
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editing ? 'Edit category' : 'New category'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2.25} sx={{ mt: 0.5 }}>
              {formError && (
                <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{formError}</Alert>
              )}
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
                fullWidth
                autoFocus
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)} color="inherit" disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving || !form.name.trim()}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create category'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete category?"
        message={
          <>
            “{toDelete?.name}” will be deleted. Tasks and time logs in this category are kept but become
            uncategorized.
          </>
        }
        loading={deleteCategory.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </Box>
  );
};

export default CategoriesPage;
